/**
 * Satış süreci sözleşmeleri (M3 WP 3.1/3.2/3.4/3.7): fırsat, teklif, sipariş, tedarikçi siparişi; durumlar, izinli geçişler,
 * girdi şemaları ve görünümler. Sözleşme belgesi: `docs/spec/castinflow/sales-documents.md`.
 * Kaynaklar: keşif tur 2 (`docs/spec/castinflow/kesif/2026-09-18-tur2/sonuc.md`, akışlar F02–F09), ÜK-2/ÜK-6/ÜK-9.
 */
import { z } from 'zod';
import { lineCampaignSchema } from './campaigns.js';
import { PRODUCT_KINDS } from './procurement.js';
import { MONEY_POLICY, VAT_RATE_POLICY, currencySchema, decimalString } from './decimal.js';
import { REQUIRED_FIELD_MESSAGE } from './zod-tr.js';
import { DISCOUNT_KINDS, LINE_QUANTITY_POLICY, PRICING_VAT_MODES, } from './pricing.js';
const uuid = z.guid();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'tarih YYYY-AA-GG olmalı');
const text = (max) => z.string().trim().max(max);
const optText = (max) => z
    .string()
    .trim()
    .max(max)
    .transform((s) => (s === '' ? null : s))
    .nullable();
// ---------------------------------------------------------------------------------------------------------------------
// Fırsat
// ---------------------------------------------------------------------------------------------------------------------
export const OPPORTUNITY_STATUSES = [
    'open_active',
    'open_on_hold',
    'won',
    'lost',
    'abandoned',
    'cancelled',
];
export const OPEN_OPPORTUNITY_STATUSES = [
    'open_active',
    'open_on_hold',
];
export const isClosedOpportunity = (s) => !OPEN_OPPORTUNITY_STATUSES.includes(s);
export const OPPORTUNITY_STATUS_LABELS = {
    open_active: 'Açık · Aktif',
    open_on_hold: 'Açık · Beklemede',
    won: 'Kazanıldı',
    lost: 'Kaybedildi',
    abandoned: 'Terk edildi',
    cancelled: 'İptal edildi',
};
/** Sistem satış süreci aşamaları (keşif: varsayılan 4 aşama). Tenant etiket/sıra/eşik ezmesi `opportunity_stages`'ta. */
export const SYSTEM_OPPORTUNITY_STAGES = [
    { key: 'introduction', label: 'Tanışma' },
    { key: 'needs_analysis', label: 'İhtiyaç belirleme' },
    { key: 'proposal', label: 'Teklif hazırlama' },
    { key: 'negotiation', label: 'Pazarlık' },
];
export const lookupKeySchema = z.string().regex(/^[a-z][a-z0-9_]{1,39}$/, 'geçersiz anahtar');
/** Kayıp sebepleri (keşifte 5 seçenek görüldü, adları kaydedilmedi → ÜK: sistem listesi, tenant ekleyebilir). */
export const SYSTEM_LOST_REASONS = [
    { key: 'price', label: 'Fiyat' },
    { key: 'competitor', label: 'Rakip tercih edildi' },
    { key: 'no_budget', label: 'Bütçe yok' },
    { key: 'no_decision', label: 'Karar verilmedi' },
    { key: 'other', label: 'Diğer' },
];
/** Gidişat (keşif: 4 seçenek, adları kaydedilmedi → ÜK). */
export const OPPORTUNITY_OUTLOOKS = ['very_good', 'good', 'uncertain', 'bad'];
export const OUTLOOK_LABELS = {
    very_good: 'Çok iyi',
    good: 'İyi',
    uncertain: 'Belirsiz',
    bad: 'Kötü',
};
const amountSchema = decimalString(MONEY_POLICY);
export const opportunityCoreInputSchema = z
    .object({
    name: text(200).min(1, 'konu gerekli'),
    companyId: uuid.nullable().optional(),
    contactId: uuid.nullable().optional(),
    amount: amountSchema.optional(),
    currency: currencySchema.optional(),
    probability: z.number().int().min(0).max(100).nullable().optional(),
    startDate: isoDate.nullable().optional(),
    expectedCloseDate: isoDate.nullable().optional(),
    stageKey: lookupKeySchema.optional(),
    outlook: z.enum(OPPORTUNITY_OUTLOOKS).nullable().optional(),
    details: optText(10000).optional(),
    ownerId: uuid.optional(),
})
    .strict();
export const createOpportunityInputSchema = z
    .object({
    core: opportunityCoreInputSchema,
    customValues: z.record(z.string(), z.unknown()).prefault({}),
})
    .strict();
export const updateOpportunityInputSchema = z
    .object({
    core: opportunityCoreInputSchema.partial(),
})
    .strict();
export const opportunityStatusInputSchema = z
    .object({
    status: z.enum(OPPORTUNITY_STATUSES),
    /** Kapanış tarihi (kapalı durumlarda; verilmezse tenant saat dilimine göre bugün) */
    closedOn: isoDate.optional(),
    /** Yalnız kazanıldı; boşsa fırsat tutarı devralınır (ÜK-6) */
    realizedAmount: amountSchema.nullable().optional(),
    lostReasonKey: lookupKeySchema.nullable().optional(),
    lostReasonDetail: optText(2000).optional(),
})
    .strict();
export const opportunityStageInputSchema = z.object({ stageKey: lookupKeySchema }).strict();
export const opportunityStageSchema = z.object({
    key: z.string(),
    label: z.string(),
    description: z.string().nullable(),
    stallDays: z.number().int().nullable(),
    sortOrder: z.number().int(),
    isActive: z.boolean(),
    system: z.boolean(),
});
export const opportunityProfileSchema = z.object({
    kind: z.literal('opportunity'),
    name: z.string(),
    companyId: z.string().nullable(),
    contactId: z.string().nullable(),
    amount: z.string(),
    currency: z.string(),
    probability: z.number().nullable(),
    /**
     * G3 (sahip kararı A): olasılığın kaynağı ve güncelliği — en son değiştiği an ve değiştiren (migration 0292). Eski kayıtlarda
     * boş ("kayıtlı değil"; varsayımla doldurulmaz). Alan yoksa önceki sunucu sürümü.
     */
    probabilitySetAt: z.string().nullable().optional(),
    probabilitySetBy: z.object({ id: z.string(), name: z.string() }).nullable().optional(),
    weightedAmount: z.string().nullable(),
    startDate: z.string().nullable(),
    expectedCloseDate: z.string().nullable(),
    status: z.enum(OPPORTUNITY_STATUSES),
    stageKey: z.string(),
    stageChangedAt: z.string(),
    stalled: z.boolean(),
    realizedAmount: z.string().nullable(),
    lostReasonKey: z.string().nullable(),
    lostReasonDetail: z.string().nullable(),
    closedOn: z.string().nullable(),
    outlook: z.enum(OPPORTUNITY_OUTLOOKS).nullable(),
    details: z.string().nullable(),
});
// ---------------------------------------------------------------------------------------------------------------------
// Belgeler: teklif, sipariş, tedarikçi siparişi
// ---------------------------------------------------------------------------------------------------------------------
export const SALES_DOCUMENT_TYPES = ['proposal', 'order', 'purchase_order'];
export const PROPOSAL_STATUSES = [
    'draft',
    'final',
    'accepted',
    'rejected',
    'abandoned',
    'withdrawn',
    'on_hold',
];
export const PROPOSAL_STATUS_LABELS = {
    draft: 'Taslak',
    final: 'Final',
    accepted: 'Kabul edildi',
    rejected: 'Reddedildi',
    abandoned: 'Terk edildi',
    withdrawn: 'Geri çekildi',
    on_hold: 'Beklemede',
};
/**
 * Teklif durum geçişleri (Castinflow kararı; keşif F05'e göre uyarlanmış — sözleşme §4.2).
 * - `draft → final` yalnız `finalize` eylemiyle (onay gerekiyorsa onaylı revizyon; revizyon kilitlenir, PDF üretilir).
 * - Final revizyon YERİNDE DEĞİŞTİRİLMEZ: değişiklik `revise` eylemiyle yeni taslak revizyondur (`final → draft` yoktur).
 * - `accepted` son durumdur (siparişe dönüştürülebilir); `rejected/abandoned/withdrawn` yeniden açılmaz, yalnız revize edilir.
 */
export const PROPOSAL_STATUS_TRANSITIONS = {
    draft: ['abandoned', 'withdrawn', 'on_hold'],
    final: ['accepted', 'rejected', 'abandoned', 'withdrawn', 'on_hold'],
    on_hold: ['abandoned', 'withdrawn'],
    accepted: [],
    rejected: [],
    abandoned: [],
    withdrawn: [],
};
/** Beklemedeki teklif kaldığı yere döner: kilitli revizyonu varsa `final`, yoksa `draft` (`resume` eylemi). */
export const REVISABLE_PROPOSAL_STATUSES = [
    'final',
    'on_hold',
    'rejected',
];
export const APPROVAL_STATES = [
    'not_required',
    'required',
    'pending',
    'approved',
    'rejected',
];
export const APPROVAL_STATE_LABELS = {
    not_required: 'Onay gerekmiyor',
    required: 'Onay gerekli',
    pending: 'Onay bekliyor',
    approved: 'Onaylandı',
    rejected: 'Onay reddedildi',
};
export const ORDER_STATUSES = ['open', 'completed', 'cancelled'];
export const ORDER_STAGES = ['waiting', 'preparing', 'shipped'];
export const ORDER_STATUS_LABELS = {
    open: 'Açık',
    completed: 'Tamamlandı',
    cancelled: 'İptal',
};
export const ORDER_STAGE_LABELS = {
    waiting: 'Bekliyor',
    preparing: 'Hazırlanıyor',
    shipped: 'Sevk edildi',
};
/** Sipariş: açıkken aşama serbest; `open → completed | cancelled`; tamamlanan/iptal son durumdur. */
export const ORDER_STATUS_TRANSITIONS = {
    open: ['completed', 'cancelled'],
    completed: [],
    cancelled: [],
};
export const PURCHASE_ORDER_STATUSES = ORDER_STATUSES;
export const DOCUMENT_NO_PREFIX = {
    proposal: 'TKL',
    order: 'SPR',
    purchase_order: 'TSP',
};
export function documentNo(type, recordNo) {
    if (recordNo === null)
        return DOCUMENT_NO_PREFIX[type];
    return `${DOCUMENT_NO_PREFIX[type]}-${String(recordNo).padStart(5, '0')}`;
}
// Satır girdisi
export const salesLineInputSchema = z.discriminatedUnion('kind', [
    z
        .object({
        kind: z.literal('heading'),
        name: text(300).min(1, 'başlık gerekli'),
    })
        .strict(),
    z
        .object({
        kind: z.literal('item'),
        productId: uuid,
        /** Ürün adı yerine belgeye özel ad (boşsa ürün adı anlık görüntüsü) */
        name: optText(300).optional(),
        description: optText(4000).optional(),
        quantity: decimalString(LINE_QUANTITY_POLICY),
        /** Boşsa ürünün fiyatı belge para birimine göre çözülür (resolveProductPrice) */
        unitPrice: amountSchema.nullable().optional(),
        discount: z
            .object({
            kind: z.enum(DISCOUNT_KINDS),
            value: z.string().max(40),
        })
            .strict()
            .nullable()
            .optional(),
        vatRate: decimalString(VAT_RATE_POLICY).nullable().optional(),
        secondTaxRate: decimalString(VAT_RATE_POLICY).nullable().optional(),
        importCostPerUnit: amountSchema.nullable().optional(),
        optional: z.boolean().optional(),
    })
        .strict(),
]);
export const SALES_LINES_MAX = 500;
export const salesTermsSchema = z
    .object({
    intro: optText(8000).optional(),
    closing: optText(8000).optional(),
    paymentTerms: optText(2000).optional(),
    deliveryTerms: optText(2000).optional(),
    notes: optText(8000).optional(),
})
    .strict();
export const salesContentInputSchema = z
    .object({
    vatMode: z.enum(PRICING_VAT_MODES),
    documentDiscount: z
        .object({ kind: z.enum(DISCOUNT_KINDS), value: z.string().max(40) })
        .strict()
        .nullable()
        .default(null),
    lines: z.array(salesLineInputSchema).max(SALES_LINES_MAX),
    terms: salesTermsSchema.prefault({}),
})
    .strict();
export const createProposalInputSchema = z
    .object({
    subject: text(200).min(1, 'konu gerekli'),
    opportunityId: uuid.nullable().optional(),
    companyId: uuid.nullable().optional(),
    contactId: uuid.nullable().optional(),
    currency: currencySchema.optional(),
    proposalDate: isoDate.optional(),
    validUntil: isoDate.nullable().optional(),
    ownerId: uuid.optional(),
    /** Teklif şablonu (verilmezse tenant varsayılanı; o da yoksa galeri "Sade") */
    templateId: uuid.nullable().optional(),
    /** Verilmezse fırsat satırlarından/boş başlar */
    content: salesContentInputSchema.optional(),
})
    .strict();
export const updateProposalHeaderInputSchema = z
    .object({
    subject: text(200).min(1).optional(),
    companyId: uuid.nullable().optional(),
    contactId: uuid.nullable().optional(),
    proposalDate: isoDate.optional(),
    validUntil: isoDate.nullable().optional(),
    templateId: uuid.nullable().optional(),
})
    .strict();
export const saveRevisionInputSchema = z
    .object({
    /** Taslak revizyonun sürümü (optimistic concurrency) */
    expectedVersion: z.number().int().min(1),
    content: salesContentInputSchema,
})
    .strict();
export const proposalStatusInputSchema = z
    .object({
    status: z.enum(PROPOSAL_STATUSES),
    reason: optText(2000).optional(),
})
    .strict();
export const submitApprovalInputSchema = z
    .object({ expectedVersion: z.number().int().min(1), note: optText(2000).optional() })
    .strict();
/**
 * K-M7-50.4 ÜRÜN KARARI: onay talebi REDDEDİLİRKEN gerekçe (not) ZORUNLUDUR — talep eden neyi düzelteceğini bilmeli ve karar
 * izi gerekçesiz kalmamalı. Onayda not isteğe bağlıdır. Satış belgesi ve form belgesi onayları aynı kuralı kullanır.
 */
export function requireRejectionNote(v, ctx) {
    if (v.decision === 'reject' && (v.note ?? '').trim() === '')
        ctx.addIssue({ code: 'custom', path: ['note'], message: REQUIRED_FIELD_MESSAGE });
}
export const decideApprovalInputSchema = z
    .object({
    decision: z.enum(['approve', 'reject']),
    /** Onaylayanın gördüğü revizyon ve içerik özeti (yarışta başka içeriği onaylamayı engeller) */
    revisionNo: z.number().int().min(1),
    contentHash: z.string().regex(/^[0-9a-f]{64}$/),
    /** Karar notu; redde ZORUNLU (K-M7-50.4) */
    note: optText(2000).optional(),
})
    .strict()
    .superRefine(requireRejectionNote);
export const finalizeProposalInputSchema = z
    .object({ expectedVersion: z.number().int().min(1) })
    .strict();
export const convertProposalInputSchema = z
    .object({
    orderDate: isoDate.optional(),
    deliveryDate: isoDate,
    deliveryAddress: text(1000).min(1, 'teslimat adresi gerekli'),
    billingAddress: optText(1000).optional(),
    stage: z.enum(ORDER_STAGES).nullable().optional(),
})
    .strict();
export const orderStatusInputSchema = z
    .object({
    status: z.enum(ORDER_STATUSES).optional(),
    stage: z.enum(ORDER_STAGES).nullable().optional(),
    reason: optText(2000).optional(),
})
    .strict();
export const createOrderInputSchema = z
    .object({
    subject: text(200).min(1, 'konu gerekli'),
    companyId: uuid,
    contactId: uuid.nullable().optional(),
    opportunityId: uuid.nullable().optional(),
    currency: currencySchema.optional(),
    orderDate: isoDate.optional(),
    deliveryDate: isoDate,
    deliveryAddress: text(1000).min(1, 'teslimat adresi gerekli'),
    billingAddress: optText(1000).optional(),
    prepayment: amountSchema.nullable().optional(),
    stage: z.enum(ORDER_STAGES).nullable().optional(),
    ownerId: uuid.optional(),
    content: salesContentInputSchema,
})
    .strict();
/** ÜK-9 parçalı sipariş: satır no → miktar; kalan miktarı aşamaz. */
export const splitOrderInputSchema = z
    .object({
    lines: z
        .array(z
        .object({
        lineNo: z.number().int().min(1),
        quantity: decimalString(LINE_QUANTITY_POLICY),
    })
        .strict())
        .min(1)
        .max(SALES_LINES_MAX),
    deliveryDate: isoDate.optional(),
})
    .strict();
export const createPurchaseOrderInputSchema = z
    .object({
    subject: text(200).min(1, 'konu gerekli'),
    supplierId: uuid,
    contactId: uuid.nullable().optional(),
    sourceOrderId: uuid.nullable().optional(),
    currency: currencySchema.optional(),
    orderDate: isoDate.optional(),
    deliveryDate: isoDate.nullable().optional(),
    ownerId: uuid.optional(),
    content: salesContentInputSchema,
})
    .strict();
// Görünümler
export const salesItemViewSchema = z.object({
    lineNo: z.number().int(),
    kind: z.enum(['item', 'heading']),
    productId: z.string().nullable(),
    productCode: z.string().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    quantity: z.string().nullable(),
    unitLabel: z.string().nullable(),
    unitPrice: z.string().nullable(),
    discount: z.object({ kind: z.enum(DISCOUNT_KINDS), value: z.string() }).nullable(),
    vatRate: z.string().nullable(),
    secondTaxRate: z.string().nullable(),
    importCostPerUnit: z.string().nullable(),
    optional: z.boolean(),
    grossNet: z.string().nullable(),
    lineDiscount: z.string().nullable(),
    net: z.string().nullable(),
    discountedNet: z.string().nullable(),
    /** Maliyet yalnız `products.cost_view` yeteneğiyle döner */
    costPrice: z.string().nullable().optional(),
    /** Satıra uygulanan kampanya (elle indirim varsa uygulanmaz); kilitli revizyonda donmuştur */
    campaign: lineCampaignSchema.nullable().default(null),
    /** G5: satır oluşturulduğu andaki ürün türü (anlık görüntü); eski satırda null. Alan yoksa sunucu önceki sürümdür. */
    productKind: z.enum(PRODUCT_KINDS).nullable().optional(),
});
export const salesRevisionViewSchema = z.object({
    revisionNo: z.number().int(),
    version: z.number().int(),
    locked: z.boolean(),
    lockedAt: z.string().nullable(),
    contentHash: z.string().nullable(),
    vatMode: z.enum(PRICING_VAT_MODES),
    currency: z.string(),
    moneyScale: z.number().int(),
    documentDiscount: z.object({ kind: z.enum(DISCOUNT_KINDS), value: z.string() }).nullable(),
    terms: salesTermsSchema,
    totals: z.custom(),
    lines: z.array(salesItemViewSchema),
});
export const renditionViewSchema = z.object({
    revisionNo: z.number().int(),
    status: z.enum(['pending', 'ready', 'failed']),
    byteSize: z.number().nullable(),
    pageCount: z.number().nullable(),
    renderMs: z.number().nullable(),
    queueMs: z.number().nullable(),
    completedAt: z.string().nullable(),
});
export const approvalReasonSchema = z.object({
    code: z.enum([
        'always',
        'amount_threshold',
        'min_amount',
        'discount_threshold',
        'max_discount',
        'min_margin',
        'margin_unknown',
        'currency_unverifiable',
        'stock_insufficient',
    ]),
    /** Eşik değeri (gösterim); tutar değerleri tenant para biriminde */
    limit: z.string().nullable(),
});
/**
 * Onaya gönderim anında yazılan karar bağlamı (K-M3-27): onaylayan neyi ve hangi müşteriyle onayladığını görür. Müşteri
 * yalnız ad/unvan (belge anlık görüntüsünün alt kümesi) — iletişim bilgisi, adres, kişi, not, finansal geçmiş YOK.
 */
export const approvalContextSchema = z.object({
    documentNo: z.string(),
    subject: z.string(),
    grandTotal: z.string(),
    currency: z.string(),
    party: z.object({ name: z.string(), tradeName: z.string().nullable() }).nullable(),
});
export const approvalRequestViewSchema = z.object({
    id: z.string(),
    revisionNo: z.number().int(),
    contentHash: z.string(),
    status: z.enum(['pending', 'approved', 'rejected', 'withdrawn', 'superseded']),
    reasons: z.array(approvalReasonSchema),
    requestedBy: z.string(),
    requestedAt: z.string(),
    requestNote: z.string().nullable(),
    decidedBy: z.string().nullable(),
    decidedAt: z.string().nullable(),
    decisionNote: z.string().nullable(),
    /** Çağıran bu talebi karara bağlayabilir mi (sunucu kararı) */
    canDecide: z.boolean(),
    /** Onaya gönderim anındaki karar bağlamı (eski taleplerde null) */
    context: approvalContextSchema.nullable(),
});
export const proposalProfileSchema = z.object({
    kind: z.literal('proposal'),
    documentNo: z.string(),
    subject: z.string(),
    opportunityId: z.string().nullable(),
    companyId: z.string().nullable(),
    contactId: z.string().nullable(),
    status: z.enum(PROPOSAL_STATUSES),
    statusReason: z.string().nullable(),
    currency: z.string(),
    proposalDate: z.string(),
    validUntil: z.string().nullable(),
    currentRevisionNo: z.number().int(),
    finalRevisionNo: z.number().int().nullable(),
    amount: z.string(),
    grandTotal: z.string(),
    approvalState: z.enum(APPROVAL_STATES),
    acceptedAt: z.string().nullable(),
    orderId: z.string().nullable(),
    templateId: z.string().nullable().default(null),
});
export const orderProfileSchema = z.object({
    kind: z.literal('order'),
    documentNo: z.string(),
    subject: z.string(),
    opportunityId: z.string().nullable(),
    companyId: z.string(),
    contactId: z.string().nullable(),
    sourceProposalId: z.string().nullable(),
    sourceRevisionNo: z.number().int().nullable(),
    parentOrderId: z.string().nullable(),
    status: z.enum(ORDER_STATUSES),
    stage: z.enum(ORDER_STAGES).nullable(),
    statusReason: z.string().nullable(),
    orderDate: z.string(),
    deliveryDate: z.string(),
    deliveryAddress: z.string(),
    billingAddress: z.string().nullable(),
    prepayment: z.string().nullable(),
    approvedBy: z.string().nullable(),
    currency: z.string(),
    currentRevisionNo: z.number().int(),
    amount: z.string(),
    grandTotal: z.string(),
});
export const purchaseOrderProfileSchema = z.object({
    kind: z.literal('purchase_order'),
    documentNo: z.string(),
    subject: z.string(),
    supplierId: z.string(),
    contactId: z.string().nullable(),
    sourceOrderId: z.string().nullable(),
    status: z.enum(ORDER_STATUSES),
    orderDate: z.string(),
    deliveryDate: z.string().nullable(),
    currency: z.string(),
    currentRevisionNo: z.number().int(),
    amount: z.string(),
    grandTotal: z.string(),
});
/** Belge ayrıntısı (`GET /sales/:type/:id/document`): profil + güncel revizyon + onay + çıktılar + izinli eylemler. */
export const salesDocumentViewSchema = z.object({
    id: z.string(),
    type: z.enum(SALES_DOCUMENT_TYPES),
    version: z.string(),
    profile: z.union([proposalProfileSchema, orderProfileSchema, purchaseOrderProfileSchema]),
    revision: salesRevisionViewSchema,
    revisions: z.array(z.object({
        revisionNo: z.number().int(),
        locked: z.boolean(),
        lockedAt: z.string().nullable(),
        grandTotal: z.string(),
    })),
    approval: approvalRequestViewSchema.nullable(),
    renditions: z.array(renditionViewSchema),
    actions: z.array(z.string()),
    /**
     * Belgenin müşterisi/tedarikçisi, çağıranın görebildiği ölçüde (K-M3-27). `record`: şirket kaydını okuyabiliyor (bağlantı
     * verilebilir); `document`: kilitli revizyonun anlık görüntüsü; `approval`: güncel revizyonun onaya sunulduğu andaki
     * bağlam. Şirket kaydı okunamıyorsa yalnız ad/unvan gelir, `companyId` gelmez. Görünür bilgi yoksa null.
     */
    customer: z
        .object({
        name: z.string(),
        tradeName: z.string().nullable(),
        source: z.enum(['record', 'document', 'approval']),
        companyId: z.string().nullable(),
        revisionNo: z.number().int().nullable(),
    })
        .nullable(),
});
/** `GET /sales/approvals/decisions`: çağıranın verdiği kararlar; belgeye bağlantı yalnız belge hâlâ okunabiliyorsa. */
export const approvalDecisionItemSchema = z.object({
    approvalId: z.string(),
    status: z.enum(['approved', 'rejected']),
    revisionNo: z.number().int(),
    decidedAt: z.string(),
    decisionNote: z.string().nullable(),
    context: approvalContextSchema.nullable(),
    documentId: z.string().nullable(),
});
/** Bağlamsal gezinme (`GET /sales/related/:type/:id`): yalnız çağıranın görebildiği ilişkili kayıtlar. */
export const RELATED_RELATIONS = [
    'opportunity',
    'proposal',
    'source_proposal',
    'order',
    'parent_order',
    'split_part',
    'contract',
    'purchase_order',
    'source_order',
];
export const relatedRecordSchema = z.object({
    relation: z.enum(RELATED_RELATIONS),
    type: z.string(),
    id: z.string(),
    title: z.string(),
    status: z.string().nullable(),
});
export const relatedRecordsSchema = z.object({ data: z.array(relatedRecordSchema) });
/** Parçalı sipariş planı (`GET /sales/order/:id/split-plan`): satır başına sipariş/ayrılan/kalan miktar ve parçalar. */
export const splitPlanSchema = z.object({
    orderId: z.string(),
    parentOrderId: z.string().nullable(),
    canSplit: z.boolean(),
    /** Parçalanamama sebebi (kullanıcı metni) */
    blockedReason: z.string().nullable(),
    lines: z.array(z.object({
        lineNo: z.number().int(),
        name: z.string(),
        unitLabel: z.string().nullable(),
        quantity: z.string(),
        allocated: z.string(),
        remaining: z.string(),
        /** Tutar olarak satır indirimi olan satır parçalanamaz */
        splittable: z.boolean(),
    })),
    parts: z.array(z.object({
        id: z.string(),
        title: z.string(),
        status: z.string(),
        stage: z.string().nullable(),
        deliveryDate: z.string().nullable(),
        grandTotal: z.string(),
    })),
});
//# sourceMappingURL=sales.js.map